import isNull from 'lodash/isNull';
import type {
	ColumnCounts,
	EpicCardType,
	EstimationInfo,
} from '@atlassian/jira-backlog-epics-store';
import { ff } from '@atlassian/jira-feature-flagging';
import { PARENT_HIERARCHY_TYPE } from '@atlassian/jira-issue-type-hierarchies';
import { defaultTimeTrackingOptions } from '@atlassian/jira-time-tracking-formatter';
import {
	CREATE_ISSUE_PERMISSION,
	DEFAULT_PARENT_ISSUE_TYPE_NAME,
	EDIT_ISSUE_PERMISSION,
} from '../../../common/constants';
import type {
	EpicPanelEpics_boardScope_boardLocation_Project as BoardLocationProject,
	EpicPanelEpics_boardScope_issueParents as Epic,
	EpicPanelEpics_boardScope_issueParents_estimationInfo as Estimation,
	EpicPanelEpics_boardScope_board_columns as Column,
} from './__generated_apollo__/EpicPanelEpics';
import type {
	EpicQueryResponse,
	FullEpic,
	IssueTypeResponse,
	TransformedEpicQueryResponse,
} from './types';

const isEpicLoadingBecauseOfOptimisticCreation = (epicId: string | number): boolean =>
	Number(epicId) < 0;

export const transformEstimationInfo = (estimation: Estimation): EstimationInfo | null => {
	const {
		details: { estimationType, name },
		childrenEstimationStats: { doneEstimate, totalEstimate, percentageCompleted, notEstimated },
	} = estimation;

	if (
		isNull(estimationType) ||
		isNull(name) ||
		isNull(doneEstimate) ||
		isNull(totalEstimate) ||
		isNull(percentageCompleted) ||
		isNull(notEstimated)
	) {
		return null;
	}

	return {
		estimationType,
		name,
		doneEstimate,
		totalEstimate,
		percentageCompleted: percentageCompleted / 100,
		notEstimated,
	};
};

const transformCard = (
	{ id, key, summary, color, childrenInfo, estimationInfo, startDate, dueDate }: Epic,
	columnCounts: { toDo: number; inProgress: number; done: number },
	isLoading: boolean,
	hasError: boolean,
): EpicCardType => {
	const epicData = {
		id: Number(id),
		key,
		name: summary,
		color: isNull(color) ? undefined : color,
		isLoading:
			isEpicLoadingBecauseOfOptimisticCreation(id) ||
			(ff('jsw-epic-panel-performance-async-epic-details') && (isLoading || !childrenInfo)),
		hasError: ff('jsw-epic-panel-performance-async-epic-details') && hasError,
		startDate: isNull(startDate) ? undefined : startDate,
		dueDate: isNull(dueDate) ? undefined : dueDate,
	};

	const shouldHideProgress =
		ff('jsw-epic-panel-performance-async-epic-details') && (isLoading || hasError);

	const { todoStats, inProgressStats, doneStats } = childrenInfo || {};
	const toDo =
		!shouldHideProgress && todoStats && !isNull(todoStats.issueCount) ? todoStats.issueCount : 0;
	const inProgress =
		!shouldHideProgress && inProgressStats && !isNull(inProgressStats.issueCount)
			? inProgressStats.issueCount
			: 0;
	const done =
		!shouldHideProgress && doneStats && !isNull(doneStats.issueCount) ? doneStats.issueCount : 0;

	return {
		epic: epicData,
		progress: {
			status: {
				toDo: {
					issueCount: toDo,
					columnCount: columnCounts.toDo,
				},
				inProgress: {
					issueCount: inProgress,
					columnCount: columnCounts.inProgress,
				},
				done: {
					issueCount: done,
					columnCount: columnCounts.done,
				},
			},
			totalIssues: toDo + inProgress + done,
		},
		estimation: estimationInfo ? transformEstimationInfo(estimationInfo) : null,
	};
};

const getColumnsCounts = (columns: Column[]) => {
	const toDo = columns.filter((column) => column.isInitial).length;
	const inProgress = columns.filter((column) => !column.isInitial && !column.isDone).length;
	const done = columns.filter((column) => column.isDone).length;

	return {
		toDo,
		inProgress,
		done,
	};
};

export const transformEpic = ({
	isCompanyManaged,
	isLoading,
	hasError,
	data,
}: {
	isCompanyManaged: boolean;
	isLoading: boolean;
	hasError: boolean;
	data?: EpicQueryResponse;
}): TransformedEpicQueryResponse => {
	const defaultReturn = {
		...(isCompanyManaged && { timeTrackingOptions: defaultTimeTrackingOptions }),
		projectId: '0',
		isRankingEnabled: !isCompanyManaged,
		cards: [],
		epicIssueType: {
			id: 0,
			name: DEFAULT_PARENT_ISSUE_TYPE_NAME,
			hasRequiredFields: false,
		},
		currentUserHasCreatePermission: false,
		currentUserHasEditPermission: false,
	};

	if (!data) {
		return defaultReturn;
	}

	const {
		globalConfig,
		boardScope: {
			issueParents,
			projectLocation,
			currentUser: { permissions },
			boardLocation,
			board,
		},
	} = data;

	let epicIssueType: IssueTypeResponse | undefined;
	let projectId = '';

	if (isCompanyManaged) {
		epicIssueType = globalConfig?.issueParentTypeConfig;
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		projectId = (boardLocation as BoardLocationProject)?.id ?? '';
	} else if (projectLocation) {
		const { issueTypes, id } = projectLocation;

		projectId = id;
		epicIssueType = issueTypes.find(
			({ hierarchyLevelType }) => hierarchyLevelType === PARENT_HIERARCHY_TYPE,
		);
	}

	if (epicIssueType) {
		const currentUserHasCreatePermission = permissions.includes(CREATE_ISSUE_PERMISSION);
		const currentUserHasEditPermission = permissions.includes(EDIT_ISSUE_PERMISSION);

		const columns = board?.columns ?? [];
		const columnCounts = getColumnsCounts(columns);
		const cards = issueParents.map((card) =>
			transformCard(card, columnCounts, isLoading, hasError),
		);

		return {
			...(data.jira && { timeTrackingOptions: data.jira.timeTrackingOptions }),
			projectId,
			isRankingEnabled: board ? !!board.rankCustomFieldId : true,
			cards,
			epicIssueType: {
				id: epicIssueType.id,
				name: epicIssueType.name,
				hasRequiredFields: epicIssueType.hasRequiredFields ?? false,
			},
			currentUserHasCreatePermission,
			currentUserHasEditPermission,
		};
	}

	return defaultReturn;
};

/* -------------------------------------------------------------------------- */

export const transformFullCard = (
	{ id, key, summary, color, childrenInfo, estimationInfo, startDate, dueDate }: FullEpic,
	columnCounts: ColumnCounts,
): EpicCardType => {
	const epicData = {
		id: Number(id),
		key,
		name: summary,
		color: isNull(color) ? undefined : color,
		isLoading: isEpicLoadingBecauseOfOptimisticCreation(id),
		hasError: false,
		startDate: isNull(startDate) ? undefined : startDate,
		dueDate: isNull(dueDate) ? undefined : dueDate,
	};

	const { todoStats, inProgressStats, doneStats } = childrenInfo || {};
	const toDo = todoStats && !isNull(todoStats.issueCount) ? todoStats.issueCount : 0;
	const inProgress =
		inProgressStats && !isNull(inProgressStats.issueCount) ? inProgressStats.issueCount : 0;
	const done = doneStats && !isNull(doneStats.issueCount) ? doneStats.issueCount : 0;

	return {
		epic: epicData,
		progress: {
			status: {
				toDo: {
					issueCount: toDo,
					columnCount: columnCounts.toDo,
				},
				inProgress: {
					issueCount: inProgress,
					columnCount: columnCounts.inProgress,
				},
				done: {
					issueCount: done,
					columnCount: columnCounts.done,
				},
			},
			totalIssues: toDo + inProgress + done,
		},
		estimation: estimationInfo ? transformEstimationInfo(estimationInfo) : null,
	};
};
